import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { authSelector } from '../../features/auth/slice';
import { PUBLIC_BASE } from './config';
import PublicLayout from '../../components/public/PublicLayout';
import { Download, ChevronDown, FileSpreadsheet, FileText, Music } from 'lucide-react';
import { GENRE_MAP } from '../../data/genres';

const ALBUM_TYPE_MAP = { 1: '싱글', 2: 'EP', 3: '정규', 4: '컴필레이션' };

function fmtPhone(c) { return [c?.phone1, c?.phone2, c?.phone3].filter(Boolean).join('-'); }
function fmtEmail(c) { if (!c?.emailId || !c?.emailDomain) return ''; return `${c.emailId}@${c.emailDomain}`; }
function fmtDateKo(d) {
  if (!d) return '';
  const dt = new Date(d);
  return `${dt.getFullYear()}년 ${dt.getMonth() + 1}월 ${dt.getDate()}일`;
}
function fmtDateHeader(d) {
  if (!d) return '';
  const dt = new Date(d);
  return `${String(dt.getFullYear()).slice(2)}. ${String(dt.getMonth()+1).padStart(2,'0')}. ${String(dt.getDate()).padStart(2,'0')}.`;
}

export default function PublicReviewViewPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { me } = useSelector(authSelector.selectAuthState);
  const currentPage = parseInt(new URLSearchParams(location.search).get('page')) || 1;

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [resultModalOpen, setResultModalOpen] = useState(false);
  const [broadcasters, setBroadcasters] = useState([]);
  const [results, setResults] = useState({});
  const [currentDetailId, setCurrentDetailId] = useState(null);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [receiptUrl, setReceiptUrl] = useState('');
  const [dlOpen, setDlOpen] = useState(false);
  const [dlLoading, setDlLoading] = useState('');
  const dlRef = useRef(null);

  const isAdmin = (me?.userType === 'client' && (me?.adminYn === 'Y' || me?.superAdminYn === 'Y'))
    || (me?.userType === 'member' && me?.reviewAdminYn === 'Y');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`/api/public/reviewApply/${id}`);
        setData(res.data);
      } catch (e) {
        console.error(e);
        alert('데이터 불러오기 실패');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  useEffect(() => {
    const handler = (e) => { if (dlRef.current && !dlRef.current.contains(e.target)) setDlOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (loading) return (
    <PublicLayout>
      <div className="flex justify-center items-center h-64"><div className="animate-spin rounded-full h-10 w-10 border-t-2 border-sky-500 mr-3" /><span className="text-sm text-slate-500">불러오는 중...</span></div>
    </PublicLayout>
  );
  if (!data) return <PublicLayout><div className="py-20 text-center text-slate-500">데이터가 없습니다.</div></PublicLayout>;

  const { reviewApply, album, member, songs } = data;
  const hasWaiting = (songs || []).some((s) => s.reviewApplyResults?.some((r) => r.status === 'WAITING'));

  const openResultModal = async (detailId) => {
    try {
      const [bcRes, resultRes] = await Promise.all([
        axios.get(`/api/public/reviewApply/broadcaster`),
        axios.get(`/api/public/reviewApply/${detailId}/result`),
      ]);
      const bcList = bcRes.data.map((b) => ({ id: b.id, name: b.name }));
      setBroadcasters(bcList);
      const init = {};
      resultRes.data.forEach((r) => { init[r.broadcasterId] = { status: r.status, remark: r.remark }; });
      bcList.forEach((b) => { if (!init[b.id]) init[b.id] = { status: 'WAITING', remark: '' }; });
      setResults(init);
      setCurrentDetailId(detailId);
      setResultModalOpen(true);
    } catch (e) {
      alert('심의 결과 불러오기 실패');
    }
  };

  const saveResults = async () => {
    try {
      await axios.put(`/api/public/reviewApply/${currentDetailId}/result`, {
        results: Object.entries(results).map(([bid, r]) => ({
          broadcasterId: Number(bid), status: r.status, remark: r.remark || '',
        })),
      });
      alert('심의결과가 저장되었습니다.');
      setResultModalOpen(false);
      // 새로고침
      const res = await axios.get(`/api/public/reviewApply/${id}`);
      setData(res.data);
    } catch (e) {
      alert(e.response?.data?.message || '저장 실패');
    }
  };

  const deleteReview = async () => {
    if (!window.confirm('정말 삭제할까요?')) return;
    try {
      await axios.delete(`/api/public/reviewApply/${reviewApply.id}`);
      alert('삭제되었습니다.');
      navigate(`${PUBLIC_BASE}/review?page=${currentPage}`);
    } catch (e) {
      alert('삭제 실패');
    }
  };

  const openReceipt = async () => {
    try {
      setReceiptLoading(true);
      const res = await axios.get(`/api/public/reviewApply/${reviewApply.id}/receipt`);
      if (!res.data?.receiptUrl) { alert('영수증 정보가 없습니다.'); return; }
      setReceiptUrl(res.data.receiptUrl);
      setReceiptOpen(true);
    } catch (e) {
      alert('영수증 불러오기 실패');
    } finally {
      setReceiptLoading(false);
    }
  };

  const artistName = album?.artistName || '';
  const albumName = album?.albumName || '';
  const releaseDate = album?.releaseDate || reviewApply?.createdAt;
  const albumTypeLabel = ALBUM_TYPE_MAP[album?.albumTypeId] || '';
  const physicalYn = album?.physicalAlbumYn === 'Y' ? 'Y' : 'N';
  // 연락처/이메일은 신청한 회원(member) 프로필에서 가져옴
  const contactInfo = member || {};

  const handleDownloadExcel = async () => {
    setDlOpen(false); setDlLoading('excel');
    try {
      const ExcelJS = (await import('exceljs')).default || (await import('exceljs'));
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet('심의신청정보');
      ws.columns = [
        { width: 16 }, { width: 18 }, { width: 16 }, { width: 28 },
        { width: 13 }, { width: 25 }, { width: 18 }, { width: 18 },
        { width: 18 }, { width: 28 },
      ];
      const THIN = { style: 'thin' };
      const border = { top: THIN, bottom: THIN, left: THIN, right: THIN };
      const labelFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9D9D9' } };
      const headerFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } };
      const center = { horizontal: 'center', vertical: 'middle', wrapText: true };
      const stLabel = (cell, text) => { cell.value = text; cell.fill = labelFill; cell.border = border; cell.font = { bold: true, size: 10 }; cell.alignment = center; };
      const stValue = (cell, text) => { cell.value = text ?? ''; cell.border = border; cell.font = { size: 10 }; cell.alignment = center; };

      // 행1: 신청자 + 연락처 + 이메일 (한 행)
      ws.getRow(1).height = 22;
      stLabel(ws.getCell('A1'), '신청자'); stValue(ws.getCell('B1'), album?.clientName || contactInfo.name || ''); ws.mergeCells('B1:C1');
      stLabel(ws.getCell('D1'), '연락처'); stValue(ws.getCell('E1'), fmtPhone(contactInfo)); ws.mergeCells('E1:F1');
      stLabel(ws.getCell('G1'), '이메일'); stValue(ws.getCell('H1'), fmtEmail(contactInfo)); ws.mergeCells('H1:J1');

      // 행2: 여백
      ws.getRow(2).height = 8;

      // 행3: 아티스트명 | 앨범명 | 발매일 | 제작일
      ws.getRow(3).height = 22;
      stLabel(ws.getCell('A3'), '아티스트명'); stValue(ws.getCell('B3'), artistName); ws.mergeCells('B3:C3');
      stLabel(ws.getCell('D3'), '앨범명'); stValue(ws.getCell('E3'), albumName); ws.mergeCells('E3:F3');
      stLabel(ws.getCell('G3'), '발매일'); stValue(ws.getCell('H3'), fmtDateKo(releaseDate));
      stLabel(ws.getCell('I3'), '제작일'); stValue(ws.getCell('J3'), fmtDateKo(reviewApply?.createdAt));

      // 행4: 음반형태 | 피지컬앨범 | 음반장르 | 유통사
      ws.getRow(4).height = 22;
      stLabel(ws.getCell('A4'), '음반형태'); stValue(ws.getCell('B4'), albumTypeLabel);
      stLabel(ws.getCell('C4'), '피지컬\n앨범 유/무'); stValue(ws.getCell('D4'), physicalYn === 'Y' ? '유' : '무');
      stLabel(ws.getCell('E4'), '음반장르'); stValue(ws.getCell('F4'), GENRE_MAP[album?.genreId] || ''); ws.mergeCells('F4:G4');
      stLabel(ws.getCell('H4'), '유통사'); stValue(ws.getCell('I4'), '핑크페이퍼'); ws.mergeCells('I4:J4');

      // 행5: 제작사
      ws.getRow(5).height = 22;
      stLabel(ws.getCell('A5'), '제작사'); stValue(ws.getCell('B5'), album?.riakProducerName || album?.clientName || ''); ws.mergeCells('B5:J5');

      // 행6: 여백
      ws.getRow(6).height = 8;

      // 행7: 곡 테이블 헤더
      ws.getRow(7).height = 36;
      const songHeaders = [
        '타이틀곡', '우선순위', '트랙',
        '곡 제목',
        '피처링', '피처링 아티스트\n최근 발매 음원',
        '편곡', '작사', '작곡', '실연 (연주가/악기명)',
      ];
      const cols = ['A','B','C','D','E','F','G','H','I','J'];
      songHeaders.forEach((h, i) => {
        const cell = ws.getCell(`${cols[i]}7`);
        cell.value = h; cell.fill = headerFill; cell.border = border;
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 }; cell.alignment = center;
      });

      // 행8~: 곡 데이터 (곡 수만큼)
      const sortedSongs = [...(songs || [])].sort((a, b) => (a.track||0) - (b.track||0));
      for (let i = 0; i < sortedSongs.length; i++) {
        const s = sortedSongs[i]; const rowIdx = 8 + i; ws.getRow(rowIdx).height = 20;
        const vals = s ? [s.titleYn==='Y'?'O':'', s.woneumYn==='Y'?'O':'', s.track||'', s.nameKr||'', s.featuring?'O':'', s.featuringLatestRelease||'', s.arrangerKr||'', s.writerKr||'', s.composerKr||'', s.performance||''] : Array(10).fill('');
        vals.forEach((v, ci) => {
          const cell = ws.getCell(`${cols[ci]}${rowIdx}`);
          cell.value = v; cell.border = border;
          cell.alignment = center;
          cell.font = { size: 10 };
        });
      }

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `심의신청정보_${artistName}_${albumName}.xlsx`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { console.error(e); alert('엑셀 생성 실패'); }
    finally { setDlLoading(''); }
  };

  const handleDownloadWord = async () => {
    setDlOpen(false); setDlLoading('word');
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('token');
      const res = await fetch(`${apiBase}/reviewApply/${id}/download-word`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.message || 'Word 생성 실패'); }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `가사_${artistName}_${albumName}.zip`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { console.error(e); alert(e.message || 'Word 생성 실패'); }
    finally { setDlLoading(''); }
  };

  const handleDownloadAudio = async () => {
    setDlOpen(false); setDlLoading('audio');
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('token');
      const res = await fetch(`${apiBase}/reviewApply/${id}/download-audio`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.message || '음원 다운로드 실패'); }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `음원_${artistName}_${albumName}.zip`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { console.error(e); alert(e.message || '음원 다운로드 실패'); }
    finally { setDlLoading(''); }
  };

  return (
    <PublicLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 md:py-8">
        <h1 className="text-xl sm:text-2xl font-bold text-white text-center mb-8">신청서 조회</h1>

        <div className="space-y-6 max-w-3xl mx-auto">
          {/* 앨범 정보 */}
          <div className="grid grid-cols-1 gap-4">
            <ROField label="신청일" value={reviewApply?.createdAt?.slice(0, 10)} extraClass="bg-amber-500/10 font-bold" />
            <ROField label="진행상황" value={hasWaiting ? '심의중' : '심의완료'} extraClass={`bg-amber-500/10 font-bold ${hasWaiting ? 'animate-pulse text-red-500' : 'animate-pulse text-sky-500'}`} />
            {isAdmin && <ROField label="회원명" value={member?.name} />}
            <ROField label="앨범명" value={album?.albumName} />
            <ROField label="권리사명" value={album?.clientName} />
            <ROField label="아티스트명" value={album?.artistName} />
            <ROField label="발매일" value={album?.releaseDate} />
            <ROField label="앨범 타입" value={ALBUM_TYPE_MAP[album?.albumTypeId] || ''} />
            <ROField label="피지컬앨범 유무" value={album?.physicalAlbumYn === 'Y' ? 'Y' : 'N'} />
            <ROField label="RIAK 아이디" value={album?.riakId} />
            <ROField label="RIAK 제작사명" value={album?.riakProducerName} />
          </div>

          {/* 곡 정보 */}
          <div className="flex items-center my-10">
            <div className="flex-grow border-t border-white/[0.08]" />
            <span className="px-3 text-slate-300 font-bold">곡 정보</span>
            <div className="flex-grow border-t border-white/[0.08]" />
          </div>

          <div className="border border-white/[0.08] rounded-2xl p-4 space-y-6 bg-white/[0.03]">
            {!songs?.length && <p className="text-sm text-slate-400">곡 정보가 없습니다.</p>}
            {songs.map((s) => (
              <div key={s.id} className="rounded-xl border border-white/[0.08] p-4 space-y-3 bg-[#182334]">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <ROField label="타이틀YN" value={s.titleYn} />
                  <ROField label="트랙" value={s.track} />
                  <ROField label="곡명" value={s.nameKr} />
                  <ROField label="작사" value={s.writerKr} />
                  <ROField label="작곡" value={s.composerKr} />
                  <ROField label="편곡" value={s.arrangerKr} />
                </div>
                {s.fileUrl && (
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">음원 듣기</label>
                    <audio controls src={s.fileUrl} className="w-full" />
                  </div>
                )}
                <ROField label="피처링" value={s.featuring} />
                <ROField label="피처링 아티스트의 최근 음원" value={s.featuringLatestRelease} />
                <ROField label="실연(연주자명/악기명)" value={s.performance} />
                {!(s.nameKr || '').includes('Inst.') && (
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">가사</label>
                    <textarea value={s.lyrics || ''} readOnly rows={5}
                      className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-white/[0.03] text-slate-300 text-sm focus:outline-none resize-none" />
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">원음방송 선택</label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={s.woneumYn === 'Y'} disabled className="peer hidden" />
                      <span className="w-6 h-6 flex items-center justify-center border border-white/[0.08] rounded-full peer-checked:bg-sky-500 peer-checked:border-sky-500 text-white text-sm">✓</span>
                      <span className="text-sm font-semibold text-slate-200">원음방송</span>
                    </label>
                  </div>
                  <div className="flex items-center">
                    <button
                      onClick={() => openResultModal(s.reviewApplyDetailId)}
                      className={`w-full px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
                        hasWaiting ? 'bg-rose-500/20 text-rose-400' : 'bg-sky-500/20 text-sky-400 hover:bg-sky-500/30'
                      }`}>
                      {isAdmin ? '심의결과 입력' : hasWaiting ? '심의중' : '심의결과 확인'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* 버튼 */}
          <div className="flex flex-wrap justify-between items-center gap-2 mt-6">
            {/* 다운로드 드롭다운 (관리자 전용) */}
            {isAdmin && (
              <div className="relative" ref={dlRef}>
                <button
                  onClick={() => setDlOpen((p) => !p)}
                  disabled={!!dlLoading}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-violet-500/20 hover:bg-violet-500/30 text-violet-300 transition-colors">
                  <Download size={15} />
                  {dlLoading === 'excel' ? '엑셀 생성 중…' : dlLoading === 'word' ? 'Word 생성 중…' : dlLoading === 'audio' ? '음원 다운로드 중…' : '다운로드'}
                  <ChevronDown size={13} className={`transition-transform ${dlOpen ? 'rotate-180' : ''}`} />
                </button>
                {dlOpen && (
                  <div className="absolute left-0 top-full mt-1 w-44 bg-[#1e2d42] border border-white/10 rounded-xl shadow-xl z-50 overflow-hidden">
                    <button onClick={handleDownloadExcel} className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-slate-200 hover:bg-white/10 transition-colors">
                      <FileSpreadsheet size={14} className="text-emerald-400" /> 심의신청정보 (Excel)
                    </button>
                    <button onClick={handleDownloadWord} className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-slate-200 hover:bg-white/10 transition-colors">
                      <FileText size={14} className="text-sky-400" /> 가사 (Word)
                    </button>
                    <button onClick={handleDownloadAudio} className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-slate-200 hover:bg-white/10 transition-colors">
                      <Music size={14} className="text-amber-400" /> 음원 (Zip)
                    </button>
                  </div>
                )}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2 ml-auto">
            {isAdmin && (
              <>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" checked={reviewApply.showEditYn === 'Y'}
                    onChange={async (e) => {
                      const val = e.target.checked ? 'Y' : 'N';
                      try {
                        await axios.put(`/api/public/reviewApply/${reviewApply.id}/showEdit`, { showEditYn: val });
                        setData((p) => ({ ...p, reviewApply: { ...p.reviewApply, showEditYn: val } }));
                      } catch { alert('저장 실패'); }
                    }} />
                  <div className="w-12 h-6 bg-slate-500 rounded-full peer-checked:bg-sky-500 transition-colors" />
                  <span className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform peer-checked:translate-x-6" />
                  <span className="ml-2 text-sm text-slate-200 whitespace-nowrap">{reviewApply.showEditYn === 'Y' ? '수정 ON' : '수정 OFF'}</span>
                </label>
                <button onClick={() => navigate(`${PUBLIC_BASE}/review/${reviewApply.id}/edit?page=${currentPage}`)} className="px-4 py-2 rounded-xl text-sm font-semibold bg-sky-600 text-white hover:bg-sky-500 transition-colors">수정</button>
              </>
            )}
            {!isAdmin && reviewApply.showEditYn === 'Y' && (
              <button onClick={() => navigate(`${PUBLIC_BASE}/review/${reviewApply.id}/edit?page=${currentPage}`)} className="px-4 py-2 rounded-xl text-sm font-semibold bg-sky-600 text-white hover:bg-sky-500 transition-colors">수정</button>
            )}
            <button onClick={openReceipt} disabled={receiptLoading} className="px-4 py-2 rounded-xl text-sm font-semibold bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 transition-colors">
              {receiptLoading ? '불러오는 중...' : '영수증'}
            </button>
            {isAdmin && <button onClick={deleteReview} className="px-4 py-2 rounded-xl text-sm font-semibold bg-rose-500/20 text-rose-400 hover:bg-rose-500/30 transition-colors">삭제</button>}
            <button onClick={() => navigate(`${PUBLIC_BASE}/review?page=${currentPage}`)} className="px-4 py-2 rounded-xl text-sm font-semibold bg-slate-700 text-slate-300 hover:bg-slate-600 transition-colors">목록</button>
            </div>
          </div>
        </div>
      </div>

      {/* 심의결과 모달 */}
      {resultModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[60]" onClick={() => setResultModalOpen(false)}>
          <div className="bg-[#182334] rounded-2xl p-6 w-full max-w-lg mx-4 border border-white/[0.08]" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-center mb-4 text-white">{isAdmin ? '심의결과 입력' : '방송사별 심의 결과'}</h2>
            {isAdmin && (
              <div className="flex gap-2 mb-4">
                <button onClick={() => setResults((p) => { const n = {}; Object.keys(p).forEach((k) => { n[k] = { ...p[k], status: 'Y' }; }); return n; })}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-sky-500/20 text-sky-400 hover:bg-sky-500/30">전체 통과</button>
                <button onClick={() => setResults((p) => { const n = {}; Object.keys(p).forEach((k) => { n[k] = { ...p[k], status: 'N' }; }); return n; })}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-rose-500/20 text-rose-400 hover:bg-rose-500/30">전체 거절</button>
              </div>
            )}
            <div className="space-y-3 max-h-[520px] overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:transparent [&::-webkit-scrollbar-thumb]:bg-slate-600 [&::-webkit-scrollbar-thumb]:rounded-full">
              {broadcasters.map((b) => (
                <div key={b.id} className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 border border-white/[0.08] p-3 rounded-xl">
                  <span className="font-bold text-white text-sm">📻 {b.name}</span>
                  <div className="flex flex-col sm:flex-row gap-2 flex-1 justify-end items-center">
                    {isAdmin ? (
                      <>
                        {['WAITING', 'Y', 'N'].map((st) => (
                          <button key={st} onClick={() => setResults((p) => ({ ...p, [b.id]: { ...p[b.id], status: st } }))}
                            className={`px-3 py-1 rounded-xl text-xs font-bold transition-colors ${
                              results[b.id]?.status === st
                                ? st === 'Y' ? 'bg-sky-500 text-white' : st === 'N' ? 'bg-red-500 text-white' : 'bg-slate-500 text-white'
                                : 'bg-white/5 text-slate-400 hover:bg-white/10'
                            }`}>
                            {st === 'Y' ? '통과' : st === 'N' ? '거절' : '심의중'}
                          </button>
                        ))}
                        <input type="text" value={results[b.id]?.remark || ''} placeholder="사유"
                          onChange={(e) => setResults((p) => ({ ...p, [b.id]: { ...p[b.id], remark: e.target.value } }))}
                          className="w-32 sm:w-40 px-2 py-1 text-sm rounded-xl border border-white/[0.08] bg-[#1a2b42] text-slate-200 focus:outline-none" />
                      </>
                    ) : (
                      <>
                        <span className={`px-3 py-1 rounded-xl text-white text-xs font-bold ${
                          results[b.id]?.status === 'Y' ? 'bg-sky-500' : results[b.id]?.status === 'N' ? 'bg-red-500' : 'bg-slate-500'
                        }`}>
                          {results[b.id]?.status === 'Y' ? '통과' : results[b.id]?.status === 'N' ? '거절' : '심의중'}
                        </span>
                        <input type="text" readOnly value={results[b.id]?.remark || ''} placeholder="사유"
                          className="w-32 sm:w-40 px-2 py-1 text-sm rounded-xl border border-white/[0.08] bg-white/[0.03] text-slate-200 focus:outline-none" />
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              {isAdmin && <button onClick={saveResults} className="px-4 py-2 rounded-xl text-sm font-semibold bg-sky-600 text-white hover:bg-sky-500">저장</button>}
              <button onClick={() => setResultModalOpen(false)} className="px-4 py-2 rounded-xl text-sm font-semibold bg-slate-700 text-slate-300 hover:bg-slate-600">닫기</button>
            </div>
          </div>
        </div>
      )}

      {/* 영수증 모달 */}
      {receiptOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[60]" onClick={() => setReceiptOpen(false)}>
          <div className="bg-[#182334] rounded-2xl w-full max-w-3xl mx-4 overflow-hidden border border-white/[0.08]" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.08]">
              <span className="font-bold text-white">결제 영수증</span>
              <button className="text-slate-400 hover:text-white" onClick={() => setReceiptOpen(false)}>✕</button>
            </div>
            <div className="w-full h-[75vh] bg-white/[0.03]">
              {receiptUrl ? <iframe title="receipt" src={receiptUrl} className="w-full h-full" /> : <div className="h-full flex items-center justify-center text-slate-400">영수증 URL이 없습니다.</div>}
            </div>
            <div className="px-4 py-3 border-t border-white/[0.08] flex justify-end">
              <button onClick={() => setReceiptOpen(false)} className="px-4 py-2 rounded-xl text-sm font-semibold bg-slate-700 text-slate-300 hover:bg-slate-600">닫기</button>
            </div>
          </div>
        </div>
      )}
    </PublicLayout>
  );
}

function ROField({ label, value, extraClass }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">{label}</label>
      <input type="text" value={typeof value === 'string' || typeof value === 'number' ? value : ''} readOnly
        className={`w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-white/5 text-slate-400 text-sm focus:outline-none ${extraClass || ''}`} />
    </div>
  );
}
